import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { supabase } from "../lib/supabase";
import { llm } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { buildChatSystemPrompt } from "../lib/chatPrompt";
import MessageBubble from "../components/chat/MessageBubble";
import ChatInput from "../components/chat/ChatInput";
import LoadingOverlay from "../components/LoadingOverlay";

export default function ChatPage() {
  const { responseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [partnerName, setPartnerName] = useState("");
  const [isRealtimeMode, setIsRealtimeMode] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const partnerIdRef = useRef(null);
  const messagesRef = useRef([]);
  const systemPromptRef = useRef("");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingText]);

  useEffect(() => {
    async function init() {
      const { data: response } = await supabase
        .from("responses")
        .select("post_id, responder_id")
        .eq("id", responseId)
        .single();

      if (!response) {
        setLoading(false);
        return;
      }

      const { data: post } = await supabase
        .from("posts")
        .select(
          "author_id, type, category, title, description, urgency, needed_by_start, cp_estimate_min, cp_estimate_max"
        )
        .eq("id", response.post_id)
        .single();

      if (!post) {
        setLoading(false);
        return;
      }

      const partnerId =
        user.id === post.author_id
          ? response.responder_id
          : post.author_id;
      partnerIdRef.current = partnerId;

      const [{ data: partner }, { data: currentUserData }] =
        await Promise.all([
          supabase
            .from("users")
            .select(
              "id, display_name, bio, rank, community_points, rating_avg, rating_count, is_verified, is_seed"
            )
            .eq("id", partnerId)
            .single(),
          supabase
            .from("users")
            .select("display_name, is_seed")
            .eq("id", user.id)
            .single(),
        ]);

      if (partner) {
        setPartnerName(partner.display_name);
        const realtimeMode =
          currentUserData?.is_seed && partner.is_seed;
        setIsRealtimeMode(realtimeMode);

        if (!realtimeMode) {
          systemPromptRef.current = buildChatSystemPrompt(
            partner,
            post,
            currentUserData?.display_name || "the other person"
          );
        }
      }

      const { data: msgs } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("response_id", responseId)
        .order("created_at", { ascending: true });

      setMessages(msgs || []);
      setLoading(false);
    }

    init();
  }, [responseId, user.id]);

  useEffect(() => {
    if (!isRealtimeMode || loading) return;

    const channel = supabase
      .channel(`chat:${responseId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "chat_messages",
          filter: `response_id=eq.${responseId}`,
        },
        (payload) => {
          if (payload.new.sender_id === user.id) return;
          setMessages((prev) => {
            if (prev.some((m) => m.id === payload.new.id)) return prev;
            return [...prev, payload.new];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isRealtimeMode, responseId, loading]);

  const sendMessage = useCallback(async (text) => {
    const { data: newMsg } = await supabase
      .from("chat_messages")
      .insert({
        response_id: responseId,
        sender_id: user.id,
        body: text,
        is_llm_generated: false,
      })
      .select()
      .single();

    if (newMsg) {
      setMessages((prev) => [...prev, newMsg]);
    }

    if (!isRealtimeMode) {
      setStreaming(true);
      setStreamingText("");

      const allMessages = [...messagesRef.current, newMsg];
      const llmMessages = [
        { role: "system", content: systemPromptRef.current },
        ...allMessages.map((m) => ({
          role: m.sender_id === user.id ? "user" : "assistant",
          content: m.body,
        })),
      ];

      let fullResponse = "";
      try {
        await llm.streamChat(llmMessages, (chunk) => {
          fullResponse += chunk;
          setStreamingText(fullResponse);
        });
      } catch {
        fullResponse = "Sorry, I'm having trouble responding right now.";
      }

      const { data: llmMsg } = await supabase
        .from("chat_messages")
        .insert({
          response_id: responseId,
          sender_id: partnerIdRef.current,
          body: fullResponse,
          is_llm_generated: true,
        })
        .select()
        .single();

      if (llmMsg) {
        setMessages((prev) => [...prev, llmMsg]);
      }

      setStreamingText("");
      setStreaming(false);
    }
  }, [isRealtimeMode, responseId, user.id]);

  if (loading) {
    return <LoadingOverlay />;
  }

  return (
    <div className="flex flex-col h-screen bg-linen">
      <div className="flex items-center gap-3 px-4 py-3 bg-cream border-b border-stone">
        <button onClick={() => navigate(-1)} className="text-walnut-light hover:text-walnut">
          <ArrowLeft size={20} />
        </button>
        <div>
          <p className="font-medium text-walnut text-sm">{partnerName || "Chat"}</p>
          {!isRealtimeMode && (
            <p className="text-xs text-walnut-light">AI-simulated</p>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 && (
          <p className="text-center text-walnut-light py-8">No messages yet. Start the conversation!</p>
        )}
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} isSelf={msg.sender_id === user.id} />
        ))}
        {streaming && streamingText && (
          <div className="flex justify-start mb-3">
            <div className="max-w-[75%] px-4 py-2.5 rounded-2xl rounded-bl-md bg-cream text-walnut text-sm leading-relaxed">
              <p className="whitespace-pre-wrap">{streamingText}</p>
            </div>
          </div>
        )}
        {streaming && !streamingText && (
          <div className="flex justify-start mb-3">
            <div className="px-4 py-2.5 rounded-2xl rounded-bl-md bg-cream text-walnut-light text-sm">
              Typing...
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <ChatInput onSend={sendMessage} disabled={streaming} />
    </div>
  );
}
